/**
 * Expression validator for Power Automate expressions
 *
 * Validates the syntax of Power Automate expressions like @{...}
 */

import type { ValidationError } from "./schema-validator.js";

/**
 * Expression validation result
 */
export interface ExpressionValidationResult {
  valid: boolean;
  errors: ValidationError[];
  expressions: string[];
}

/**
 * Known Power Automate functions
 */
const KNOWN_FUNCTIONS = new Set([
  // Trigger/Action references
  "triggerBody",
  "triggerOutputs",
  "outputs",
  "body",
  "items",
  "variables",
  "parameters",
  "workflow",
  "result",
  "actionOutputs",
  "actionBody",
  "trigger",
  "actions",

  // String functions
  "concat",
  "toLower",
  "toUpper",
  "trim",
  "substring",
  "replace",
  "split",
  "length",
  "contains",
  "startsWith",
  "endsWith",
  "indexOf",
  "lastIndexOf",
  "nthIndexOf",
  "slice",
  "chunk",
  "formatNumber",
  "encodeUriComponent",
  "decodeUriComponent",
  "uriComponent",
  "uriComponentToString",
  "base64",
  "base64ToBinary",
  "base64ToString",
  "dataUri",
  "dataUriToBinary",
  "dataUriToString",
  "binary",
  "decodeDataUri",

  // Collection functions
  "first",
  "last",
  "take",
  "skip",
  "join",
  "createArray",
  "union",
  "intersection",
  "sort",
  "reverse",
  "range",
  "empty",

  // Logical functions
  "if",
  "and",
  "or",
  "not",
  "equals",
  "greater",
  "greaterOrEquals",
  "less",
  "lessOrEquals",
  "coalesce",

  // Conversion functions
  "int",
  "float",
  "string",
  "bool",
  "json",
  "xml",
  "array",
  "object",
  "decodeBase64",

  // Math functions
  "add",
  "sub",
  "mul",
  "div",
  "mod",
  "max",
  "min",
  "rand",
  "abs",
  "ceiling",
  "floor",
  "round",

  // Date/time functions
  "utcNow",
  "addDays",
  "addHours",
  "addMinutes",
  "addSeconds",
  "addToTime",
  "convertFromUtc",
  "convertToUtc",
  "convertTimeZone",
  "formatDateTime",
  "parseDateTime",
  "startOfDay",
  "startOfHour",
  "startOfMonth",
  "dayOfMonth",
  "dayOfWeek",
  "dayOfYear",
  "ticks",
  "getFutureTime",
  "getPastTime",
  "subtractFromTime",

  // Reference functions
  "guid",
  "pipeline",
  "activity",
  "dataset",
  "linkedService",

  // URI/path functions
  "uriHost",
  "uriPath",
  "uriPathAndQuery",
  "uriPort",
  "uriQuery",
  "uriScheme",
]);

/**
 * Find wrapped @{...} expressions while respecting nested braces and quoted text.
 */
function extractWrappedExpressions(text: string): string[] {
  const expressions: string[] = [];

  for (let i = 0; i < text.length - 1; i++) {
    if (text[i] !== "@") continue;
    if (text[i + 1] !== "{") continue;
    if (i > 0 && text[i - 1] === "@") continue;

    let depth = 0;
    let inString = false;
    let stringChar = "";
    let end = -1;

    for (let j = i + 2; j < text.length; j++) {
      const char = text[j];
      const prevChar = j > i + 2 ? text[j - 1] : "";

      if ((char === "'" || char === "\"") && prevChar !== "\\") {
        if (!inString) {
          inString = true;
          stringChar = char;
        } else if (char === stringChar) {
          inString = false;
        }
        continue;
      }

      if (inString) continue;

      if (char === "{") {
        depth++;
        continue;
      }

      if (char === "}") {
        if (depth === 0) {
          end = j;
          break;
        }
        depth--;
      }
    }

    if (end !== -1) {
      expressions.push(text.slice(i + 2, end));
      i = end;
    }
  }

  return expressions;
}

/**
 * Regular expression to find where a bare @function() expression STARTS.
 *
 * Only the start — the end is found by scanning (see `findBareExpressionEnd`). A regex
 * cannot locate it reliably: the previous pattern ended in a greedy `.*\)`, which
 * backtracks to the LAST `)` in the string, so an index accessor closing after it was
 * cut off. `@variables('Map')?[outputs('Key')]` was captured as
 * `variables('Map')?[outputs('Key')` and then reported as "Unbalanced brackets:
 * missing ']'" — a validation error on a perfectly valid expression.
 */
const BARE_EXPRESSION_START_REGEX = /@(?:triggerBody|triggerOutputs|outputs|body|items|variables|parameters|workflow|result|actionOutputs|actionBody|trigger|actions|concat|toLower|toUpper|trim|substring|replace|split|length|contains|startsWith|endsWith|indexOf|lastIndexOf|first|last|take|skip|join|createArray|union|intersection|empty|if|and|or|not|equals|greater|greaterOrEquals|less|lessOrEquals|coalesce|int|float|string|bool|json|xml|array|add|sub|mul|div|mod|max|min|utcNow|addDays|addHours|addMinutes|addSeconds|formatDateTime|guid|base64|encodeUriComponent|decodeUriComponent)\s*\(/g;

/**
 * Walk from the opening `(` to the true end of a bare expression.
 *
 * Tracks paren and bracket depth while respecting single-quoted string literals (where
 * `''` is an escaped quote), then keeps consuming chained accessors — `?['a']`, `.value`,
 * `[0]` — so the whole expression is returned rather than a prefix of it.
 *
 * Returns the index one past the last character, or -1 if the expression never closes.
 */
function findBareExpressionEnd(text: string, openParenIndex: number): number {
  let parens = 0;
  let brackets = 0;
  let inString = false;

  for (let i = openParenIndex; i < text.length; i++) {
    const char = text[i];

    if (inString) {
      // '' inside a literal is an escaped quote, not the end of the string
      if (char === "'") {
        if (text[i + 1] === "'") i++;
        else inString = false;
      }
      continue;
    }

    if (char === "'") {
      inString = true;
      continue;
    }
    if (char === "(") parens++;
    else if (char === ")") parens--;
    else if (char === "[") brackets++;
    else if (char === "]") brackets--;

    if (parens === 0 && brackets === 0) {
      // Balanced — but a trailing accessor continues the same expression.
      let next = i + 1;
      while (next < text.length && /\s/.test(text[next] ?? "")) next++;
      let following = text[next];

      // `?` only continues the expression when it introduces an accessor.
      if (following === "?" && (text[next + 1] === "." || text[next + 1] === "[")) {
        next += 1;
        following = text[next];
      }

      if (following === "[") {
        i = next - 1; // let the loop count the bracket
        continue;
      }
      if (following === ".") {
        // Consume the property name too, or the expression stops at the dot.
        let end = next + 1;
        while (end < text.length && /[A-Za-z0-9_]/.test(text[end] ?? "")) end++;
        if (end > next + 1) {
          i = end - 1;
          continue;
        }
      }
      return i + 1;
    }
  }

  return -1;
}

/**
 * Extract all expressions from a string.
 * Handles both @{...} wrapped and bare @function() forms.
 */
export function extractExpressions(text: string): string[] {
  const expressions = extractWrappedExpressions(text);

  BARE_EXPRESSION_START_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = BARE_EXPRESSION_START_REGEX.exec(text)) !== null) {
    const openParen = match.index + match[0].length - 1;
    const end = findBareExpressionEnd(text, openParen);
    if (end === -1) {
      // Never closes — hand the remainder to the validator so it reports the real problem.
      expressions.push(text.slice(match.index + 1));
      break;
    }
    expressions.push(text.slice(match.index + 1, end));
    BARE_EXPRESSION_START_REGEX.lastIndex = end;
  }
  BARE_EXPRESSION_START_REGEX.lastIndex = 0;

  return expressions;
}

/**
 * Extract all expressions from a value (recursively handles objects/arrays)
 */
export function extractExpressionsFromValue(value: unknown): string[] {
  const expressions: string[] = [];

  function extract(v: unknown): void {
    if (typeof v === "string") {
      expressions.push(...extractExpressions(v));
    } else if (Array.isArray(v)) {
      for (const item of v) {
        extract(item);
      }
    } else if (v !== null && typeof v === "object") {
      for (const propValue of Object.values(v)) {
        extract(propValue);
      }
    }
  }

  extract(value);
  return expressions;
}

/**
 * Check if a string contains expressions (wrapped or bare)
 */
export function hasExpression(text: string): boolean {
  BARE_EXPRESSION_START_REGEX.lastIndex = 0;
  const hasBare = BARE_EXPRESSION_START_REGEX.test(text);
  BARE_EXPRESSION_START_REGEX.lastIndex = 0;
  return extractWrappedExpressions(text).length > 0 || hasBare;
}

/**
 * Validate a single expression
 */
export function validateExpression(expression: string, path: string): ValidationError[] {
  const errors: ValidationError[] = [];

  // Check for balanced parentheses
  let parenCount = 0;
  let bracketCount = 0;
  let inString = false;
  let stringChar = "";

  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];
    const prevChar = i > 0 ? expression[i - 1] : "";

    // Handle string literals
    if ((char === "'" || char === '"') && prevChar !== "\\") {
      if (!inString) {
        inString = true;
        stringChar = char;
      } else if (char === stringChar) {
        inString = false;
      }
      continue;
    }

    if (!inString) {
      if (char === "(") parenCount++;
      if (char === ")") parenCount--;
      if (char === "[") bracketCount++;
      if (char === "]") bracketCount--;

      if (parenCount < 0) {
        errors.push({
          path,
          message: `Unbalanced parentheses: extra ')' at position ${i}`,
          severity: "error",
        });
      }

      if (bracketCount < 0) {
        errors.push({
          path,
          message: `Unbalanced brackets: extra ']' at position ${i}`,
          severity: "error",
        });
      }
    }
  }

  if (parenCount !== 0) {
    errors.push({
      path,
      message: `Unbalanced parentheses: ${parenCount > 0 ? "missing ')'" : "extra ')'"}`,
      severity: "error",
    });
  }

  if (bracketCount !== 0) {
    errors.push({
      path,
      message: `Unbalanced brackets: ${bracketCount > 0 ? "missing ']'" : "extra ']'"}`,
      severity: "error",
    });
  }

  if (inString) {
    errors.push({
      path,
      message: "Unterminated string literal",
      severity: "error",
    });
  }

  // Check for known functions
  const functionMatch = expression.match(/^(\w+)\s*\(/);
  if (functionMatch && functionMatch[1]) {
    const funcName = functionMatch[1];
    if (!KNOWN_FUNCTIONS.has(funcName)) {
      errors.push({
        path,
        message: `Unknown function: ${funcName}`,
        severity: "warning",
      });
    }
  }

  // Check for common mistakes
  if (expression.includes("..")) {
    errors.push({
      path,
      message: "Consecutive dots found - possible typo",
      severity: "warning",
    });
  }

  if (expression.includes("??")) {
    errors.push({
      path,
      message: "Use coalesce() instead of ?? operator",
      severity: "warning",
    });
  }

  return errors;
}

/**
 * Validate all expressions in a value
 */
export function validateExpressions(
  value: unknown,
  basePath: string
): ExpressionValidationResult {
  const result: ExpressionValidationResult = {
    valid: true,
    errors: [],
    expressions: [],
  };

  function validate(v: unknown, path: string): void {
    if (typeof v === "string") {
      const expressions = extractExpressions(v);
      result.expressions.push(...expressions);

      for (const expr of expressions) {
        const exprErrors = validateExpression(expr, path);
        for (const error of exprErrors) {
          if (error.severity === "error") {
            result.valid = false;
          }
          result.errors.push(error);
        }
      }
    } else if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) {
        validate(v[i], `${path}[${i}]`);
      }
    } else if (v !== null && typeof v === "object") {
      for (const [key, propValue] of Object.entries(v)) {
        validate(propValue, `${path}.${key}`);
      }
    }
  }

  validate(value, basePath);
  return result;
}

/**
 * Check if an expression references an action
 */
export function expressionReferencesAction(expression: string, actionName: string): boolean {
  // Check various patterns for action references
  const patterns = [
    `outputs('${actionName}')`,
    `body('${actionName}')`,
    `result('${actionName}')`,
    `actions('${actionName}')`,
    `actionOutputs('${actionName}')`,
    `actionBody('${actionName}')`,
    `items('${actionName}')`,
  ];

  return patterns.some((pattern) => expression.includes(pattern));
}

/**
 * Get all actions referenced by expressions in a value
 */
export function getReferencedActions(value: unknown): Set<string> {
  const referenced = new Set<string>();
  const expressions = extractExpressionsFromValue(value);

  // Pattern to extract action names from common reference functions
  const actionRefPattern = /(?:outputs|body|result|actions|actionOutputs|actionBody|items)\s*\(\s*['"]([^'"]+)['"]\s*\)/g;

  for (const expr of expressions) {
    let match: RegExpExecArray | null;
    while ((match = actionRefPattern.exec(expr)) !== null) {
      if (match[1]) referenced.add(match[1]);
    }
    actionRefPattern.lastIndex = 0;
  }

  return referenced;
}

/**
 * Check if an expression references the trigger
 */
export function expressionReferencesTrigger(expression: string): boolean {
  const triggerPatterns = [
    "triggerBody()",
    "triggerOutputs()",
    "trigger()",
  ];

  return triggerPatterns.some((pattern) => expression.includes(pattern));
}

/**
 * Validate that action references in expressions are valid
 */
export function validateActionReferences(
  expressions: string[],
  validActions: Set<string>,
  path: string
): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const expr of expressions) {
    const referenced = getReferencedActions(expr);
    for (const actionName of referenced) {
      if (!validActions.has(actionName)) {
        errors.push({
          path,
          message: `Expression references unknown action: ${actionName}`,
          severity: "error",
        });
      }
    }
  }

  return errors;
}
